import { useState, useRef } from 'react';
import { aboutData } from '@/mocks/about';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9u55ptca23r0tr1aqug';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <section>
      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
        联系我们
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
        <div>
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-phone-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1">客服电话</h4>
                <p className="text-sm text-foreground-600">{aboutData.contact.phone}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1">公司地址</h4>
                <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-wechat-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1">微信公众号</h4>
                <p className="text-sm text-foreground-600">{aboutData.contact.wechat}</p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-5 bg-background-100/80 rounded-lg">
            <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-2">工作时间</h4>
            <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00（法定节假日除外）</p>
            <p className="text-sm text-foreground-600 mt-1">我们会在24小时内回复您的咨询</p>
          </div>
        </div>

        <div>
          <form ref={formRef} id="contact-form" onSubmit={handleSubmit} data-readdy-form="">
            <div className="space-y-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-400"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-400"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-400"
                  placeholder="请输入咨询主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-400 resize-none"
                  placeholder="请输入您的留言内容（不超过500字）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formStatus === 'error' && formError && (
                <div className="p-3 rounded-md bg-primary-100/70 text-sm text-primary-800">
                  {formError}
                </div>
              )}
              {formStatus === 'success' && (
                <div className="p-3 rounded-md bg-accent-100/70 text-sm text-accent-800">
                  感谢您的留言！我们会在24小时内回复您。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : (
                  '发送留言'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}